import { Fragment, type ReactNode } from 'react';

/**
 * Telegram HTML ni ekranda ko'rsatish — `dangerouslySetInnerHTML` SIZ.
 *
 * Sabab bevosita: bu yerdagi razmetkani admin O'ZI yozadi, ya'ni uni brauzerga xom uzatish
 * admin panelida saqlanadigan XSS ochadi — bitta hisob buzilgani boshqa hamma adminning
 * sessiyasiga aylanadi. Ustiga Telegram baribir sanoqli tegni tushunadi, ya'ni to'liq HTML
 * render qilish ko'rsatilgan narsani yuboriladigan narsadan uzoqlashtirardi.
 *
 * Shuning uchun teglar element-element o'qiladi va faqat tanilganlari React tuguniga
 * aylantiriladi. Notanish teg matn bo'lib ko'rinadi — bu ham foydali: Telegram o'shanda butun
 * xabarni rad etadi, va odam buni yuborishdan oldin ko'rgani ma'qul.
 */

const RENDERED: Record<string, (children: ReactNode, href?: string) => ReactNode> = {
  b: (children) => <strong>{children}</strong>,
  strong: (children) => <strong>{children}</strong>,
  i: (children) => <em>{children}</em>,
  em: (children) => <em>{children}</em>,
  u: (children) => <u>{children}</u>,
  ins: (children) => <u>{children}</u>,
  s: (children) => <s>{children}</s>,
  strike: (children) => <s>{children}</s>,
  del: (children) => <s>{children}</s>,
  code: (children) => (
    <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.9em]">{children}</code>
  ),
  pre: (children) => (
    <pre className="overflow-x-auto rounded bg-muted p-2 font-mono text-xs">{children}</pre>
  ),
  blockquote: (children) => (
    <blockquote className="border-l-2 pl-2 text-muted-foreground">{children}</blockquote>
  ),
  'tg-spoiler': (children) => (
    <span className="rounded bg-foreground/80 text-transparent">{children}</span>
  ),
  span: (children) => <span>{children}</span>,
  // Havola ATAYLAB bosilmaydi: bu oldindan ko'rish, navigatsiya emas — va manzilda hali
  // to'ldirilmagan `{...}` turgan bo'lishi mumkin.
  a: (children, href) => (
    <span className="text-primary underline" title={href}>
      {children}
    </span>
  ),
};

const TOKEN = /<(\/?)([a-zA-Z][a-zA-Z0-9-]*)((?:\s[^>]*)?)>/g;

interface Node {
  tag: string;
  href?: string;
  children: Array<Node | string>;
}

function unescape(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&');
}

function parse(html: string): Array<Node | string> {
  const root: Node = { tag: '', children: [] };
  const stack: Node[] = [root];
  let cursor = 0;

  for (const match of html.matchAll(TOKEN)) {
    const [whole, closing, rawTag, attrs] = match;
    const tag = rawTag!.toLowerCase();
    if (!Object.hasOwn(RENDERED, tag)) continue;

    const text = html.slice(cursor, match.index);
    if (text) stack.at(-1)!.children.push(unescape(text));
    cursor = match.index + whole.length;

    if (closing) {
      // Faqat mos yopilish stackdan chiqaradi: `</b>` juftsiz kelsa uni e'tiborsiz qoldirish
      // qolgan matnni yo'qotishdan yaxshiroq.
      if (stack.length > 1 && stack.at(-1)!.tag === tag) stack.pop();
      continue;
    }

    const href = /href\s*=\s*["']([^"']*)["']/i.exec(attrs ?? '')?.[1];
    const node: Node = { tag, ...(href ? { href } : {}), children: [] };
    stack.at(-1)!.children.push(node);
    stack.push(node);
  }

  const tail = html.slice(cursor);
  if (tail) stack.at(-1)!.children.push(unescape(tail));
  return root.children;
}

function render(nodes: Array<Node | string>): ReactNode {
  return nodes.map((node, index) => {
    if (typeof node === 'string') return <Fragment key={index}>{node}</Fragment>;
    const build = RENDERED[node.tag];
    return <Fragment key={index}>{build!(render(node.children), node.href)}</Fragment>;
  });
}

export function TelegramHtmlPreview({ html }: { html: string }) {
  // `whitespace-pre-wrap` — shablondagi qator uzilishlari Telegramdagidek ko'rinishi uchun:
  // g'oliblar ro'yxati aynan shu qatorlar bilan o'qiladi.
  return <div className="whitespace-pre-wrap break-words text-sm">{render(parse(html))}</div>;
}
